import * as React from "react";

import {
  PRESENTATION_SCROLL_ACTIONS,
  usePresenterForScroll_asController,
} from "../../../Common";

import { RequestProductsSearch } from "../../model";

import { useReactPlpInitialState } from "../react-plp-intial-state-provider";
import { usePlpDataTargetUrlState } from "../plp-data-target-url-state";

import { useProductListRunningCache } from "./product-list-running-cache-provider";
import {
  useProductListState,
  useProductListState_asController,
} from "./product-list-state-provider";
import { useProductListUrlState } from "./product-list-url-state-provider";
import { useProductListUrlStateController } from "./product-list-url-state-controller-provider";
import {
  usePlpDataSourceProductListController,
  usePlpDataSourceProductListQuery,
} from "../plp-data-source-product-list-provider";
import { useProductListPagination_asController } from "./product-list-pagination-provider";

interface ProductListAutoInitializationConsumerInterface {
  hasInitialized: boolean;
}

const defaultValue =
  {} as unknown as ProductListAutoInitializationConsumerInterface;

const ProductListAutoInitializationContext = React.createContext(defaultValue);

interface ProductListAutoInitializationProviderProps {
  providerId: string;
}

const channelMobileBreakPoint = 768;

export const ProductListAutoInitializationProvider: React.FunctionComponent<
  ProductListAutoInitializationProviderProps
> = (props) => {
  const thisProviderEventNameBase = `bbnt-plp-products-provider-auto-initialization-${props.providerId}`;

  const {
    initialState: { baseQuery },
  } = useReactPlpInitialState();

  const { initiateScrollAction } = usePresenterForScroll_asController();

  const {
    transform: transformToCacheableUrlState,
    extractOptionsPortionOfUrlDataWhichIsReadyToCache,
  } = usePlpDataTargetUrlState();
  const { status, data } = usePlpDataSourceProductListQuery();
  const { requestProductList } = usePlpDataSourceProductListController();

  const { pageSize, startPage, selectedOptions, selectedSortingOption } =
    useProductListState();
  const { setStartPage } = useProductListState_asController();
  const { hasInitializedQueryState, hasConsolidatedUrlStateDataSource } =
    useProductListUrlState();
  const { facetCache } = useProductListRunningCache();
  const { handleNewProductListState } = useProductListUrlStateController();
  const { resetLoadMorePagination } = useProductListPagination_asController();

  const [hasInitialized, setHasInitialized] = React.useState(false);

  const generateDesiredProductListFromTheStateAsItIsRightNow =
    React.useCallback(
      (reinitializePageOffset: boolean) => {
        const nextUrlState = transformToCacheableUrlState({
          selectedOptions,
          facetCache,
          selectedSortingOption,
          page:
            reinitializePageOffset || hasInitialized
              ? 0
              : (data?.pages.length || 0) + startPage,
        });

        handleNewProductListState(nextUrlState);

        const optionsQuery_decoded =
          extractOptionsPortionOfUrlDataWhichIsReadyToCache(nextUrlState);

        // the api for some reason returns a different key than what it expects for categories
        if (optionsQuery_decoded.categories) {
          optionsQuery_decoded.cat_id = optionsQuery_decoded.categories;
          delete optionsQuery_decoded.categories;
        }

        let extraParameters = {};
        if (nextUrlState.sort) {
          extraParameters = {
            ...extraParameters,
            ...{
              sort: nextUrlState.sort,
            },
          };
        }

        const offset = startPage * pageSize;

        const desiredProductList: RequestProductsSearch = {
          search: "",
          max: pageSize,
          offset,
          sort: "",
          channel:
            window.innerWidth < channelMobileBreakPoint ? "mobile" : "desktop",
          backend: "fredhopper",
          ...(baseQuery || {}),
          options: optionsQuery_decoded,
          ...extraParameters,
        };

        return desiredProductList;
      },
      [
        selectedOptions,
        facetCache,
        selectedSortingOption,
        hasInitialized,
        data,
        startPage,
        pageSize,
        baseQuery,
        transformToCacheableUrlState,
        extractOptionsPortionOfUrlDataWhichIsReadyToCache,
      ]
    );
  const initialize = React.useCallback(() => {
    if (hasInitialized) {
      return;
    }

    const desiredProductList =
      generateDesiredProductListFromTheStateAsItIsRightNow(false);

    requestProductList(desiredProductList);
  }, [
    hasInitialized,
    generateDesiredProductListFromTheStateAsItIsRightNow,
    requestProductList,
  ]);
  const reinitialize = React.useCallback(() => {
    if (!hasInitialized) {
      return;
    }

    if (hasInitialized && startPage !== 0) {
      setStartPage(0);
    }

    resetLoadMorePagination();

    const desiredProductList =
      generateDesiredProductListFromTheStateAsItIsRightNow(true);

    requestProductList(desiredProductList);

    initiateScrollAction(PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_TOP);
  }, [
    hasInitialized,
    startPage,
    generateDesiredProductListFromTheStateAsItIsRightNow,
    requestProductList,
    initiateScrollAction,
    resetLoadMorePagination,
  ]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisProviderEventNameBase}-initialize`,
      initialize
    );
    window.addEventListener(
      `${thisProviderEventNameBase}-different-thing-being-requested`,
      reinitialize
    );
    return () => {
      window.removeEventListener(
        `${thisProviderEventNameBase}-initialize`,
        initialize
      );
      window.removeEventListener(
        `${thisProviderEventNameBase}-different-thing-being-requested`,
        reinitialize
      );
    };
  }, [initialize, reinitialize]);
  React.useEffect(() => {
    if (
      hasInitializedQueryState &&
      hasConsolidatedUrlStateDataSource &&
      !hasInitialized
    ) {
      window.dispatchEvent(
        new Event(`${thisProviderEventNameBase}-initialize`)
      );
    }
  }, [
    hasInitializedQueryState,
    hasConsolidatedUrlStateDataSource,
    hasInitialized,
  ]);
  React.useEffect(() => {
    const statusesWhichIndicateACompletedRequestOfSomeKindErrorOrSuccess: typeof status[] =
      ["error", "success"];
    const doesThisStatusIndicateSomeKindOfCompletion =
      statusesWhichIndicateACompletedRequestOfSomeKindErrorOrSuccess.includes(
        status
      );
    const hasThisInitialized =
      !hasInitialized && doesThisStatusIndicateSomeKindOfCompletion;
    if (hasThisInitialized) {
      setHasInitialized(true);
    }
  }, [hasInitialized, status]);
  React.useEffect(() => {
    window.dispatchEvent(
      new Event(`${thisProviderEventNameBase}-different-thing-being-requested`)
    );
  }, [selectedSortingOption, selectedOptions]);

  const value: ProductListAutoInitializationConsumerInterface = React.useMemo(
    () => ({
      hasInitialized,
    }),
    [hasInitialized]
  );
  return (
    <ProductListAutoInitializationContext.Provider value={value}>
      {props?.children || []}
    </ProductListAutoInitializationContext.Provider>
  );
};

export const useProductListAutoInitialization = () => {
  const value = React.useContext(ProductListAutoInitializationContext);
  if (value === defaultValue) {
    throw new Error(
      "useProductListAutoInitialization must be used within a ProductListAutoInitializationProvider"
    );
  }
  return value;
};
